/**
 * 列のサブツリー(列 → 条件分岐 → 分岐質問 → …)を、固定行のセルとして複製する。
 * 分岐質問もさらに条件分岐を持てるため、同じ規則で再帰的に複製する。
 *
 * 複製先ノードの同一性(id / uuid)は resolveIdentity が決める。
 * - 行の追加: 省略し、すべて新規採番する
 * - 正規化: 対応表から既存の固定行ノードの id / uuid を引き継ぐ
 *
 * 計算式・時間計測の参照張り替えは行わない。行の全セルの uuid 対応が揃ってから、
 * 呼び出し側が remapSubtreeQuestionRefs でまとめて適用する。
 */

import {
  TemplateNodeSchema,
  TemplateNodeTypeEnum,
} from '@ulysses-inc/harami_api_client'
import type { TemplateNodesDict } from 'src/exShared/types/types'
import { UUID } from 'src/exShared/util/uuid'
import { getLogicBranchQuestions, getQuestionLogicNodes } from '../selector'

/** 複製先ノードに与える同一性。 */
export type CellNodeIdentity = { id: number; uuid: string | undefined }

/** 列ノード id から、複製先に使う同一性を引く。undefined なら新規採番する。 */
export type ResolveCellIdentity = (
  columnNodeId: number,
) => CellNodeIdentity | undefined

export const cloneColumnSubtree = (
  columnNode: TemplateNodeSchema,
  templateNodes: TemplateNodesDict,
  allocateId: () => number,
  resolveIdentity: ResolveCellIdentity = () => undefined,
): {
  cellNodeId: number
  nodes: TemplateNodesDict
  /** 列側 uuid → 複製先 uuid。参照の行ローカル化に使う。 */
  uuidByColumnUuid: Map<string, string>
} => {
  const nodes: TemplateNodesDict = {}
  const uuidByColumnUuid = new Map<string, string>()

  const identityOf = (node: TemplateNodeSchema): CellNodeIdentity => {
    const identity = resolveIdentity(node.id) ?? {
      id: allocateId(),
      uuid: UUID(),
    }
    if (node.uuid && identity.uuid) {
      uuidByColumnUuid.set(node.uuid, identity.uuid)
    }
    return identity
  }

  /** 質問ノードの複製。新規質問のデフォルト値は当てず、列をそのまま引き継ぐ。 */
  const cloneQuestionNode = (
    node: TemplateNodeSchema,
    identity: CellNodeIdentity,
  ): TemplateNodeSchema => ({
    id: identity.id,
    uuid: identity.uuid,
    type: TemplateNodeTypeEnum.Question,
    nodes: [],
    question: structuredClone(node.question ?? {}),
  })

  /** 質問とその配下の条件分岐を、深さの制限なく複製する。 */
  const cloneQuestionSubtree = (questionNode: TemplateNodeSchema): number => {
    const logicIds = getQuestionLogicNodes(questionNode, templateNodes).map(
      columnLogic => {
        const branchIds = getLogicBranchQuestions(
          columnLogic,
          templateNodes,
        ).map(cloneQuestionSubtree)

        const identity = identityOf(columnLogic)
        nodes[identity.id] = {
          id: identity.id,
          uuid: identity.uuid,
          type: TemplateNodeTypeEnum.Logic,
          logic: structuredClone(columnLogic.logic ?? {}),
          nodes: branchIds,
        }
        return identity.id
      },
    )

    const identity = identityOf(questionNode)
    nodes[identity.id] = {
      ...cloneQuestionNode(questionNode, identity),
      nodes: logicIds,
    }
    return identity.id
  }

  return {
    cellNodeId: cloneQuestionSubtree(columnNode),
    nodes,
    uuidByColumnUuid,
  }
}
